import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Card } from '@design-system/Card';
import { Button } from '@design-system/Button';
import { ShimmerImage } from '@screens/SMP/components/ShimmerImage';
import { AddonPackSelector } from '@screens/ShopPage/components/AddonPackSelector';
import {
  formatPackPrice,
  getDiscountedPrice,
} from '@screens/ShopPage/utils/pricing';
import type { ProductPack } from '@viome/viome-base/src/api/Strapi/StrapiTypes';

export interface AddonCardProps {
  id: string;
  title: string;
  subtitle?: string;
  basePrice: number;
  discountAmount?: number;
  discountPercentage?: number;
  description: string;
  imageSrc: string;
  packs?: ProductPack[] | null;
  trimDecimal?: boolean;
  isProcessing?: boolean;
  onOrderNow: (quantity: number) => void;
  onViewDetails: () => void;
}

const getDefaultPackNumber = (packs: ProductPack[]): number => {
  const defaultPack = packs.find((p) => p.isDefault);
  return defaultPack?.pack ?? packs[0]?.pack ?? 1;
};

export const AddonCard = ({
  id,
  title,
  subtitle,
  basePrice,
  discountAmount = 0,
  discountPercentage = 0,
  description,
  imageSrc,
  packs,
  trimDecimal,
  isProcessing = false,
  onOrderNow,
  onViewDetails,
}: AddonCardProps) => {
  const packList = Array.isArray(packs) ? packs : [];
  const hasPacks = packList.length > 0;
  const [selectedPack, setSelectedPack] = useState(() =>
    getDefaultPackNumber(packList),
  );

  // Fallback (no packs): amount first, then percentage — same as Tests
  const fallbackPricing = getDiscountedPrice(
    basePrice,
    discountAmount,
    discountPercentage,
  );

  return (
    <Card
      className="rounded-2xl p-6 flex flex-col items-stretch text-left gap-4"
      data-testid={`shopAddOn-card-${id}`}
    >
      <div className="flex items-center gap-4">
        <ShimmerImage
          src={imageSrc}
          alt={title}
          containerClassName="size-16 shrink-0"
          className="size-16 object-contain"
        />

        <div className="flex flex-col">
          <h3 className="typography-h3 text-primary font-medium">{title}</h3>
          {subtitle ? (
            <p className="typography-body mt-1 font-serif italic text-[#4D4F64CC]">
              {subtitle}
            </p>
          ) : null}
          {/* Older UI: show amount below subtitle when packs are missing */}
          {!hasPacks ? (
            <div className="flex items-baseline gap-1.5 mt-1">
              {fallbackPricing.hasDiscount ? (
                <span className="typography-body text-tertiary line-through">
                  {formatPackPrice(fallbackPricing.originalPrice, trimDecimal)}
                </span>
              ) : null}
              <span className="typography-h3 text-[#146464]">
                {formatPackPrice(fallbackPricing.discountedPrice, trimDecimal)}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <p className="typography-body text-tertiary">{description}</p>

      <button
        type="button"
        className="flex items-center self-start typography-btn-medium text-[#146464]"
        onClick={onViewDetails}
        data-testid={`shopAddOn-viewDetails-${id}`}
      >
        Learn More
        <ChevronRight className="h-4 w-4 ml-0.5" />
      </button>

      {hasPacks ? (
        <AddonPackSelector
          packs={packList}
          basePrice={basePrice}
          selectedPack={selectedPack}
          onSelect={setSelectedPack}
          trimDecimal={trimDecimal}
          className="mt-1"
        />
      ) : null}

      <Button
        className="w-full h-14 rounded-full bg-black text-white hover:bg-black/80"
        onClick={() => onOrderNow(hasPacks ? selectedPack : 1)}
        disabled={isProcessing}
        data-testid={`shopAddOn-orderButton-${id}`}
      >
        {isProcessing ? 'Processing...' : 'Order Now'}
      </Button>
    </Card>
  );
};
